import { useEffect, useState, useCallback } from 'react';
import { validateLicenseCloud } from './api';

export type LicenseTier = 'free' | 'starter' | 'pro';

export interface LicenseState {
  tier: LicenseTier;
  status: string;
  isPro: boolean;
  isStarter: boolean;
  isExpired: boolean;
  expiresAt: string | null;
  expiresLabel: string | null;
  kind?: string | null;
  plan?: string | null;
  key?: string | null;
  body?: string | null;
  source?: string | null;
  activatedAt?: string | null;
  cloudSource?: 'cloud' | 'local-fallback' | null;
  validationSource?: 'cloud' | 'local-fallback';
}

declare global {
    interface Window {
      IndxLicense?: {
      getState?: () => Partial<LicenseState>;
      isPro?: () => boolean;
      isStarter?: () => boolean;
      deactivate?: () => void;
      activate?: (key: string, options?: { source?: string; tier?: string; expiresAt?: string | null; kind?: string | null }) => { ok: boolean; message: string };
      validateWithCloud?: (key: string) => Promise<{ ok: boolean; message: string; tier?: string }>;
      };
    }
}

function readLicenseState(): LicenseState {
  const raw = window.IndxLicense?.getState?.();
  const tier = raw?.tier === 'starter' || raw?.tier === 'pro' ? raw.tier : 'free';
  return {
    tier,
    status: raw?.status || (tier === 'free' ? 'free' : tier),
    isPro: raw?.isPro === true,
    isStarter: raw?.isStarter === true,
    isExpired: raw?.isExpired === true,
    expiresAt: raw?.expiresAt || null,
    expiresLabel: raw?.expiresLabel || null,
    kind: raw?.kind || null,
    plan: raw?.plan || null,
    key: raw?.key || null,
    body: raw?.body || null,
    source: raw?.source || null,
    activatedAt: raw?.activatedAt || null,
  };
}

export function isLicensedTier(tier: LicenseTier) {
  return tier === 'starter' || tier === 'pro';
}

export function isStarterTier(tier: LicenseTier) {
  return tier === 'starter' || tier === 'pro';
}

export function isProTier(tier: LicenseTier) {
  return tier === 'pro';
}

export function isOwnerLicense(license: Pick<LicenseState, 'tier' | 'kind' | 'isPro'>) {
  return license.kind === 'owner' && license.tier === 'pro' && license.isPro === true;
}

export function useLicense() {
  const [state, setState] = useState<LicenseState>(() => ({
    ...readLicenseState(),
    validationSource: readLicenseState().tier !== 'free' ? 'local-fallback' : 'cloud',
  }));

  useEffect(() => {
    const handleChange = () => setState((prev) => ({ ...readLicenseState(), validationSource: prev.validationSource }));
    window.addEventListener('indxlicense:changed', handleChange);
    return () => window.removeEventListener('indxlicense:changed', handleChange);
  }, []);

  useEffect(() => {
    const current = readLicenseState();
    if (!current.key) return;

    let cancelled = false;
    validateLicenseCloud(current.key).then((result) => {
      if (cancelled) return;

      if (result.ok) {
        if (window.IndxLicense?.activate) {
          window.IndxLicense.activate(current.key || '', {
            source: result.source,
            tier: result.tier,
            expiresAt: result.expiresAt,
            kind: result.kind,
          });
        }
        setState({ ...readLicenseState(), validationSource: 'cloud' });
        return;
      }

      // Cloud responded with ok:false — server is authority, don't fall back to local
      if (result.source === 'cloud' || result.source === 'offline-cache') {
        window.IndxLicense?.deactivate?.();
        setState({ ...readLicenseState(), tier: 'free', isPro: false, isStarter: false, validationSource: 'cloud' });
        return;
      }

      // Network error — use local fallback
      setState({ ...readLicenseState(), validationSource: 'local-fallback' });
    }).catch(() => {
      if (!cancelled) setState((prev) => ({ ...prev, validationSource: 'local-fallback' }));
    });

    return () => { cancelled = true; };
  }, []);

  return state;
}

export function useCloudValidation() {
  const [validating, setValidating] = useState(false);
  const [cloudResult, setCloudResult] = useState<LicenseState['cloudSource']>(null);

  const validate = useCallback(async (key: string) => {
    setValidating(true);
    try {
      const result = await validateLicenseCloud(key);
      setCloudResult(result.source === 'cloud' ? 'cloud' : 'local-fallback');

      if (result.ok && window.IndxLicense?.activate) {
        window.IndxLicense.activate(key, {
          source: result.source,
          tier: result.tier,
          expiresAt: result.expiresAt,
          kind: result.kind,
        });
      }

      return result;
    } finally {
      setValidating(false);
    }
  }, []);

  return { validate, validating, cloudResult };
}
